/**
 * The words the Prospects page uses for the scoring service's codes: priorities, signals, filter
 * buckets, confidence warnings and feedback reasons. Kept in one place so the list, the panel and the
 * filters say the same thing, and so wording is reviewed in one file (every figure and claim on this
 * page is externally shown, PRD §16). Wording that depends on which CRM the book came from is in
 * `crm-copy.ts`.
 *
 * The scoring service calls its ranks `tier1`, `tier2`, `tier3` and `untiered`, and those names stay
 * in the API, the URL and analytics. An advisor is shown "High priority", "Medium priority",
 * "Low priority" and "Low fit", because "Tier 2" has to be explained and a priority does not. Every
 * sentence that names a priority is in `priorityCopy` below, so the wording can change in one place.
 * The scoring service's own two messages that name it are in `fitScore/settings.ts`.
 */
import type { Prospecting } from '@client/api/types';

export const TIER_LABELS: Record<Prospecting.Tier, string> = {
  tier1: 'High priority',
  tier2: 'Medium priority',
  tier3: 'Low priority',
  untiered: 'Low fit',
};

export const priorityCopy = {
  howItWorks: 'How priorities are set',
  howItWorksIntro: (crm: string) =>
    `Priorities rank the contacts in ${crm} for a long-term care planning conversation. They are a planning prompt, not advice, and say nothing about anyone's health.`,
  column: 'Priority',
  whyThis: 'Why this priority',
  sectionTitle: 'Priorities',

  tabs: {
    tiered: 'Prioritized',
    untiered: 'Low fit',
    insufficientData: 'Not enough data',
    excluded: 'Excluded',
  } satisfies Record<Prospecting.ProspectView, string>,

  /** One line per priority: what puts a contact there. Shown on the cards and in the explanation. */
  rule: (tier: Exclude<Prospecting.Tier, 'untiered'>, parentRecorded: string): string =>
    ({
      tier1: `A high fit, with a parent ${parentRecorded}.`,
      tier2:
        'A high fit with an annuity, life or disability product, retirement within five years, or a meeting in the last year. Or a moderate fit with a parent on record.',
      tier3: 'A fit with no further signal, or a parent on record with a lower fit.',
    })[tier],
  lowFitRule: 'Scored as a low fit, with no parent on record. Not prioritized.',

  intro: (crm: string) => `Your ${crm} contacts, ranked for a long-term care conversation.`,
  /** The same, for a book ranked without notes or emails, which has to be said wherever the ranking is shown. */
  introDetailsOnly: (crm: string) =>
    `Your ${crm} contacts, ranked for a long-term care conversation using ${crm} details only. Notes and emails are not read, so a priority never means a known care need.`,
  noCareNeed:
    'Notes and emails are not read, so no priority reflects a known care need. Contacts who are already clients, deceased, marked do-not-contact, minors or above the issue age you set are left out, each with the reason shown.',

  movedFromHigh: 'Moved from High: over your limit',
  includesMoved: (count: number) =>
    count === 1 ? 'Medium includes 1 contact moved from High, over your limit.' : `Medium includes ${count} contacts moved from High, over your limit.`,
  noneCanBeHigh: (crm: string) =>
    `Family relationships cannot be read from this ${crm} account, so no contact can reach High priority.`,
  highNeedsParent: (parentRecorded: string, crm: string) => `High priority needs a parent ${parentRecorded} in ${crm}.`,

  settings: {
    share: 'High priority limit, as a share of your contacts (%)',
    count: 'High priority limit, as a number of contacts (optional)',
    hint: 'The smaller of the two limits applies. Contacts over it move to Medium priority.',
  },
};

export const FIT_BAND_LABELS: Record<Prospecting.FitBand, string> = {
  high: 'High fit',
  moderate: 'Moderate fit',
  low: 'Low fit',
};

export const SIGNAL_LABELS: Record<Prospecting.ProspectSignal, string> = {
  parentsInCrm: 'Parent recorded in your CRM',
  protectionProductHeld: 'Holds annuity, life or DI',
  retiringSoon: 'Retiring within 5 years',
  metRecently: 'Met in the last 12 months',
};

/** Notes that read the same for every CRM; the product note names the CRM (`crm-copy.ts`). */
export const NOTE_LABELS: Record<string, string> = {
  hasLtcPolicy: 'Recorded as having an LTC policy',
};

export const AGE_BAND_LABELS: Record<string, string> = {
  'under-50': 'Under 50',
  '50-59': '50–59',
  '60-69': '60–69',
  '70-79': '70–79',
  '80-plus': '80+',
};

export const ASSETS_BAND_LABELS: Record<string, string> = {
  'under-250k': 'Under $250K',
  '250k-1m': '$250K–$1M',
  '1m-5m': '$1M–$5M',
  '5m-plus': '$5M+',
};

export const LAST_MEETING_LABELS: Record<Prospecting.LastMeetingFilter, string> = {
  within12Months: 'Met in the last 12 months',
  over12Months: 'Last met over 12 months ago',
  none: 'No meeting recorded',
};

export const CONFIDENCE_LABELS: Record<Prospecting.ConfidenceReason, string> = {
  birthDateNotMapped: 'Date of birth is not mapped, so most contacts cannot be scored.',
  doNotContactNotMapped: 'No do-not-contact field is mapped, so opted-out contacts cannot be excluded.',
  firmAssetsOnly: 'Only assets held at your firm are recorded; household net worth is not mapped.',
  noAssets: 'No net worth or assets field is mapped.',
  manyWithTooLittleData: 'Many contacts have too few details recorded to be scored.',
  modelNotSignedOff: 'This scoring model is a preview and has not been approved for use.',
  noRelationshipSource: 'Family relationships cannot be read from your CRM, so no contact can reach High priority.',
};

/** The reasons a vote may carry. One names the CRM, so the list is built for the book's own. */
export function feedbackReasons(crmName: string): Record<'up' | 'down', { code: string; label: string }[]> {
  return {
    up: [
      { code: 'goodFit', label: 'Good fit' },
      { code: 'alreadyPlanningToCall', label: 'Already planning to call' },
      { code: 'other', label: 'Other' },
    ],
    down: [
      { code: 'deceased', label: 'Deceased (remove from list)' },
      { code: 'doNotContact', label: 'Do not contact (remove from list)' },
      { code: 'notAProspect', label: 'Not a prospect (remove from list)' },
      { code: 'alreadyCovered', label: 'Already has coverage' },
      { code: 'alreadyDiscussed', label: 'Already discussed' },
      { code: 'wrongData', label: `Details in ${crmName} are wrong` },
      { code: 'other', label: 'Other' },
    ],
  };
}

export function fullName(item: Pick<Prospecting.ProspectListItem, 'firstName' | 'lastName'>): string {
  return [item.firstName, item.lastName].filter(Boolean).join(' ') || 'Unnamed contact';
}

/** A moment, with its time: when a book was last scored is worth knowing to the minute. */
export function formatDateTime(value: string): string {
  return new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatDate(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value.length === 10 ? `${value}T00:00:00` : value);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
